/**
 * OpenTable Service
 * Handles restaurant search, availability checks, and reservation booking.
 * Routes between OpenTable live API and AI-powered recommendations based
 * on the OPENTABLE_LIVE_API feature flag.
 *
 * Data source priority (when live API enabled):
 *   1. OpenTable Live API → 2. DynamoDB Cache → 3. AI Fallback
 */

import { isFeatureEnabled } from '../config/features';
import { logger } from '../utils/logger';
import { API_BASE_URL } from '../config/environment';
import { authFetch } from '../utils/authFetch';
import { getTasteProfile, learnSignal } from './tasteProfileService';

const OPENTABLE_ENABLED = isFeatureEnabled('OPENTABLE_ENABLED');
const USE_MOCK_DATA = false; // Set to true to use static mock data

/**
 * Mock restaurant data for testing
 */
const MOCK_RESTAURANTS = [
  {
    id: 'mock-1',
    name: 'The Capital Grille',
    cuisine: 'Steakhouse',
    priceRange: '$$$$',
    rating: 4.5,
    reviewCount: 1234,
    address: '500 Crescent Ct, Dallas, TX 75201',
    city: 'Dallas',
    state: 'TX',
    zipCode: '75201',
    latitude: 32.7767,
    longitude: -96.7970,
    phone: '(214) 303-0500',
    imageUrl: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=400',
    description: 'Upscale steakhouse with classic American fare and an extensive wine list.',
    hours: {
      monday: '11:30 AM - 10:00 PM',
      tuesday: '11:30 AM - 10:00 PM',
      wednesday: '11:30 AM - 10:00 PM',
      thursday: '11:30 AM - 10:00 PM',
      friday: '11:30 AM - 11:00 PM',
      saturday: '5:00 PM - 11:00 PM',
      sunday: '5:00 PM - 9:00 PM'
    },
    distance: 0.8,
    openTableId: 'mock-1'
  },
  {
    id: 'mock-2',
    name: "Perry's Steakhouse & Grille",
    cuisine: 'Steakhouse',
    priceRange: '$$$$',
    rating: 4.6,
    reviewCount: 892,
    address: '2000 Ross Ave, Dallas, TX 75201',
    city: 'Dallas',
    state: 'TX',
    zipCode: '75201',
    latitude: 32.7890,
    longitude: -96.7980,
    phone: '(214) 871-9999',
    imageUrl: 'https://images.unsplash.com/photo-1555396273-367ea4b4bdf2?w=400',
    description: 'Sophisticated steakhouse featuring prime steaks, seafood, and craft cocktails.',
    hours: {
      monday: '11:00 AM - 10:00 PM',
      tuesday: '11:00 AM - 10:00 PM',
      wednesday: '11:00 AM - 10:00 PM',
      thursday: '11:00 AM - 10:00 PM',
      friday: '11:00 AM - 11:00 PM',
      saturday: '4:00 PM - 11:00 PM',
      sunday: '4:00 PM - 9:00 PM'
    },
    distance: 1.2,
    openTableId: 'mock-2'
  },
  {
    id: 'mock-3',
    name: 'Revolver Taco Lounge',
    cuisine: 'Mexican',
    priceRange: '$',
    rating: 4.4,
    reviewCount: 567,
    address: '2701 Main St, Dallas, TX 75226',
    city: 'Dallas',
    state: 'TX',
    zipCode: '75226',
    latitude: 32.7800,
    longitude: -96.7900,
    phone: '(214) 741-7481',
    imageUrl: 'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=400',
    description: 'Modern Mexican restaurant with creative tacos and craft cocktails.',
    hours: {
      monday: 'Closed',
      tuesday: '5:00 PM - 10:00 PM',
      wednesday: '5:00 PM - 10:00 PM',
      thursday: '5:00 PM - 10:00 PM',
      friday: '5:00 PM - 11:00 PM',
      saturday: '5:00 PM - 11:00 PM',
      sunday: '5:00 PM - 9:00 PM'
    },
    distance: 2.1,
    openTableId: 'mock-3'
  },
  {
    id: 'mock-4',
    name: 'DFW Airport - Pappadeaux Seafood Kitchen',
    cuisine: 'Seafood',
    priceRange: '$$',
    rating: 4.2,
    reviewCount: 234,
    address: 'DFW Airport, Terminal D, Gate D23',
    city: 'Dallas',
    state: 'TX',
    zipCode: '75261',
    latitude: 32.8998,
    longitude: -97.0403,
    phone: '(972) 574-3000',
    imageUrl: 'https://images.unsplash.com/photo-1559339352-11d035aa65de?w=400',
    description: 'Cajun seafood restaurant located in DFW Airport Terminal D.',
    hours: {
      monday: '5:00 AM - 10:00 PM',
      tuesday: '5:00 AM - 10:00 PM',
      wednesday: '5:00 AM - 10:00 PM',
      thursday: '5:00 AM - 10:00 PM',
      friday: '5:00 AM - 10:00 PM',
      saturday: '5:00 AM - 10:00 PM',
      sunday: '5:00 AM - 10:00 PM'
    },
    distance: 0.1,
    terminal: 'D',
    gate: 'D23',
    openTableId: 'mock-4'
  },
  {
    id: 'mock-5',
    name: 'DFW Airport - TGI Fridays',
    cuisine: 'American',
    priceRange: '$',
    rating: 3.8,
    reviewCount: 189,
    address: 'DFW Airport, Terminal A, Gate A15',
    city: 'Dallas',
    state: 'TX',
    zipCode: '75261',
    latitude: 32.8998,
    longitude: -97.0403,
    phone: '(972) 574-2000',
    imageUrl: 'https://images.unsplash.com/photo-1555396273-367ea4b4bdf2?w=400',
    description: 'Casual American restaurant in DFW Airport Terminal A.',
    hours: {
      monday: '5:00 AM - 11:00 PM',
      tuesday: '5:00 AM - 11:00 PM',
      wednesday: '5:00 AM - 11:00 PM',
      thursday: '5:00 AM - 11:00 PM',
      friday: '5:00 AM - 11:00 PM',
      saturday: '5:00 AM - 11:00 PM',
      sunday: '5:00 AM - 11:00 PM'
    },
    distance: 0.2,
    terminal: 'A',
    gate: 'A15',
    openTableId: 'mock-5'
  }
];

// ── Task 5.4: Track previous OPENTABLE_LIVE_API state for cache-clearing ─────
let _previousLiveApiState = isFeatureEnabled('OPENTABLE_LIVE_API');
// eslint-disable-next-line no-unused-vars
let _cachedAiResults = null;

/**
 * Listen for feature flag changes. When OPENTABLE_LIVE_API transitions
 * from disabled → enabled, clear locally cached AI-sourced results so
 * the next search fetches fresh data from the live API.
 */
if (typeof window !== 'undefined') {
  window.addEventListener('featureToggleChanged', (event) => {
    const { featureName, enabled } = event.detail || {};
    if (featureName === 'OPENTABLE_LIVE_API') {
      if (!_previousLiveApiState && enabled) {
        // Transitioning from disabled → enabled: clear stale AI results
        logger.debug('[OpenTableService] OPENTABLE_LIVE_API enabled — clearing cached AI results');
        _cachedAiResults = null;
      }
      _previousLiveApiState = enabled;
    }
  });
}

// ── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Calculate distance between two coordinates (Haversine formula)
 * @param {number} lat1
 * @param {number} lon1
 * @param {number} lat2
 * @param {number} lon2
 * @returns {number} Distance in miles
 */
function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 3959; // Earth radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

// ── Task 5.2: OpenTable Directory API response mapping ──────────────────────

/**
 * Convert numeric OpenTable price level (1-4) to dollar-sign string.
 * @param {number} price - 1 through 4
 * @returns {string} '$' through '$$$$'
 */
export function mapPriceRange(price) {
  const map = { 1: '$', 2: '$$', 3: '$$$', 4: '$$$$' };
  return map[price] || '$$'; // default to $$ if unexpected value
}

/**
 * Map an OpenTable Directory API response object to the internal
 * restaurant card data model used by RestaurantCard and other components.
 *
 * @param {Object} response - Single restaurant object from the Directory API
 * @returns {Object} Mapped restaurant card object
 */
export function mapOpenTableResponse(response) {
  return {
    id: response.rid,
    name: response.name,
    cuisine: response.cuisine_name,
    priceRange: mapPriceRange(response.price),
    rating: response.rating,
    reviewCount: response.num_reviews,
    address: response.address,
    latitude: response.lat,
    longitude: response.lng,
    phone: response.phone,
    imageUrl: response.photo_url,
    openTableId: response.rid,
    reservationUrl: response.reserve_url,
    dataSource: response.dataSource || 'opentable-live',
  };
}

// ── Task 5.3: Filtering and taste-profile ranking ───────────────────────────

/**
 * Filter a list of restaurants by the given criteria.
 * All active filters must be satisfied simultaneously.
 *
 * @param {Array} restaurants - Array of restaurant objects
 * @param {Object} filters
 * @param {string}  [filters.cuisine]    - Cuisine substring match (case-insensitive)
 * @param {string}  [filters.priceRange] - Exact price range match (e.g. '$$')
 * @param {number}  [filters.minRating]  - Minimum rating (inclusive)
 * @param {number}  [filters.radius]     - Max distance in miles (requires lat/lng on restaurant)
 * @param {number}  [filters.latitude]   - Reference latitude for radius filter
 * @param {number}  [filters.longitude]  - Reference longitude for radius filter
 * @returns {Array} Filtered restaurants
 */
export function filterRestaurants(restaurants, filters = {}) {
  return restaurants.filter((r) => {
    // Cuisine filter (case-insensitive substring)
    if (filters.cuisine && !(r.cuisine || '').toLowerCase().includes(filters.cuisine.toLowerCase())) {
      return false;
    }
    // Price range filter (exact match)
    if (filters.priceRange && r.priceRange !== filters.priceRange) {
      return false;
    }
    // Minimum rating filter
    if (filters.minRating != null && (r.rating == null || r.rating < filters.minRating)) {
      return false;
    }
    // Radius filter (requires reference coordinates)
    if (filters.radius != null && filters.latitude != null && filters.longitude != null) {
      if (r.latitude != null && r.longitude != null) {
        const dist = calculateDistance(filters.latitude, filters.longitude, r.latitude, r.longitude);
        if (dist > filters.radius) {
          return false;
        }
      }
    }
    return true;
  });
}

/**
 * Rank restaurants by taste profile preferences.
 * Restaurants whose cuisine matches a preferred cuisine are sorted before
 * those that don't, when other factors (rating) are equal.
 *
 * @param {Array} restaurants - Array of restaurant objects
 * @param {Object|null} tasteProfile - User taste profile (may be null)
 * @returns {Array} Sorted copy of the restaurants array
 */
export function rankByTasteProfile(restaurants, tasteProfile) {
  if (!tasteProfile || !tasteProfile.dining || !tasteProfile.dining.cuisines) {
    return [...restaurants];
  }

  const cuisineFreqs = tasteProfile.dining.cuisines; // e.g. { "Italian": 5, "Japanese": 3 }

  return [...restaurants].sort((a, b) => {
    const aCuisine = (a.cuisine || '').toLowerCase();
    const bCuisine = (b.cuisine || '').toLowerCase();

    // Find the best matching preference score for each restaurant
    const aScore = getPreferenceScore(aCuisine, cuisineFreqs);
    const bScore = getPreferenceScore(bCuisine, cuisineFreqs);

    // Higher preference score first
    if (bScore !== aScore) {
      return bScore - aScore;
    }

    // Tie-break by rating (higher first)
    const aRating = a.rating || 0;
    const bRating = b.rating || 0;
    return bRating - aRating;
  });
}

/**
 * Get the preference score for a cuisine from the frequency map.
 * Performs case-insensitive matching.
 * @param {string} cuisineLower - Lowercase cuisine name
 * @param {Object} cuisineFreqs - Frequency map { "Italian": 5, ... }
 * @returns {number} Score (0 if no match)
 */
function getPreferenceScore(cuisineLower, cuisineFreqs) {
  for (const [key, value] of Object.entries(cuisineFreqs)) {
    if (key.toLowerCase() === cuisineLower) {
      return value;
    }
  }
  return 0;
}

// ── Task 5.1: Search with OPENTABLE_LIVE_API flag routing ───────────────────

/**
 * Build URLSearchParams from the common search parameters.
 * @param {Object} params
 * @returns {URLSearchParams}
 */
function buildSearchParams(params) {
  const queryParams = new URLSearchParams();
  if (params.address) queryParams.append('address', params.address);
  if (params.hotelName) queryParams.append('hotelName', params.hotelName);
  if (params.airportCode) queryParams.append('airportCode', params.airportCode);
  if (params.city) queryParams.append('city', params.city);
  if (params.latitude && params.longitude) {
    queryParams.append('latitude', params.latitude);
    queryParams.append('longitude', params.longitude);
  }
  if (params.cuisine) queryParams.append('cuisine', params.cuisine);
  if (params.priceRange) queryParams.append('priceRange', params.priceRange);
  if (params.radius) queryParams.append('radius', params.radius);
  if (params.mealType) queryParams.append('mealType', params.mealType);
  if (params.partySize) queryParams.append('partySize', params.partySize);
  if (params.minRating) queryParams.append('minRating', params.minRating);
  return queryParams;
}

/**
 * Fetch restaurants from the OpenTable live API proxy.
 * Returns null on failure so the caller can fall through to the next source.
 */
async function fetchLiveApiRestaurants(params, tasteProfile) {
  try {
    logger.debug('[RestaurantSearch] Using OpenTable live API via proxy');
    const queryParams = buildSearchParams(params);

    const response = await authFetch(`${API_BASE_URL}/opentable/restaurants/search?${queryParams}`);

    if (!response.ok) {
      throw new Error(`OpenTable proxy error: ${response.status}`);
    }

    const dataSource = response.headers.get('X-Data-Source') || 'opentable-live';
    const data = await response.json();
    const rawRestaurants = Array.isArray(data) ? data : (data.restaurants || []);

    let restaurants = rawRestaurants.map((r) => {
      if (r.id && r.cuisine && r.priceRange) {
        return { ...r, dataSource };
      }
      return mapOpenTableResponse({ ...r, dataSource });
    });

    restaurants = filterRestaurants(restaurants, params);
    restaurants = rankByTasteProfile(restaurants, tasteProfile);

    logger.debug(`[RestaurantSearch] Proxy returned ${restaurants.length} restaurants (source: ${dataSource})`);
    return restaurants;
  } catch (error) {
    console.error('[RestaurantSearch] Live API search failed, falling back to AI:', error);
    return null;
  }
}

/**
 * Fetch restaurants from the AI-powered recommendations endpoint.
 * Returns null on failure so the caller can fall through to mock data.
 */
async function fetchAiRestaurants(params, tasteProfile) {
  try {
    logger.debug('[RestaurantSearch] Using AI-powered recommendations');
    const queryParams = buildSearchParams(params);

    const response = await authFetch(`${API_BASE_URL}/restaurant-ai?${queryParams}`);

    if (!response.ok) {
      throw new Error(`Restaurant AI API error: ${response.status}`);
    }

    const dataSource = response.headers.get('X-Data-Source') || 'ai-fallback';
    const data = await response.json();

    if (data.success && data.restaurants) {
      let restaurants = data.restaurants.map((r) => ({ ...r, dataSource }));
      restaurants = filterRestaurants(restaurants, params);
      restaurants = rankByTasteProfile(restaurants, tasteProfile);
      _cachedAiResults = restaurants;

      logger.debug(`[RestaurantSearch] AI returned ${restaurants.length} restaurants`);
      return restaurants;
    } else {
      throw new Error('Invalid response from Restaurant AI');
    }
  } catch (error) {
    console.error('[RestaurantSearch] AI search failed, falling back to mock data:', error);
    return null;
  }
}

/**
 * Return filtered mock restaurant data (backward compatibility).
 */
async function fetchMockRestaurants(params) {
  await new Promise(resolve => setTimeout(resolve, 500));

  let results = [...MOCK_RESTAURANTS];

  if (params.latitude && params.longitude) {
    results = results.map(restaurant => ({
      ...restaurant,
      distance: calculateDistance(
        params.latitude,
        params.longitude,
        restaurant.latitude,
        restaurant.longitude
      )
    })).filter(restaurant => {
      const radius = params.radius || 5;
      return restaurant.distance <= radius;
    }).sort((a, b) => a.distance - b.distance);
  }

  if (params.airportCode) {
    results = results.filter(r =>
      r.address.toLowerCase().includes(params.airportCode.toLowerCase()) ||
      (r.terminal && r.terminal.toLowerCase().includes(params.airportCode.toLowerCase()))
    );
  }

  if (params.cuisine) {
    results = results.filter(r =>
      r.cuisine.toLowerCase().includes(params.cuisine.toLowerCase())
    );
  }

  if (params.priceRange) {
    results = results.filter(r => r.priceRange === params.priceRange);
  }

  if (params.minRating) {
    results = results.filter(r => r.rating >= params.minRating);
  }

  return results;
}

/**
 * Search restaurants by location.
 *
 * Routing logic:
 *   - OPENTABLE_LIVE_API enabled  → proxy endpoint /opentable/restaurants/search
 *   - OPENTABLE_LIVE_API disabled → AI endpoint /restaurant-ai
 *
 * The X-Data-Source response header is read and attached to each restaurant
 * object as the `dataSource` field.
 *
 * @param {Object} params - Search parameters
 * @returns {Promise<Array>} Array of restaurant objects
 */
export async function searchRestaurants(params = {}) {
  if (!OPENTABLE_ENABLED) {
    return [];
  }

  // ── Task 5.3: Fetch taste profile for ranking ──
  let tasteProfile = null;
  try {
    tasteProfile = await getTasteProfile();
  } catch (err) {
    console.warn('[OpenTableService] Taste profile unavailable, proceeding without personalization:', err.message);
  }

  const useLiveApi = isFeatureEnabled('OPENTABLE_LIVE_API');

  // ── Live API path ──
  if (useLiveApi) {
    const liveResult = await fetchLiveApiRestaurants(params, tasteProfile);
    if (liveResult) return liveResult;
  }

  // ── AI-powered recommendations path ──
  const aiResult = await fetchAiRestaurants(params, tasteProfile);
  if (aiResult) return aiResult;

  // ── Mock data path — last-resort fallback when live/AI both fail.
  // Always attempted here (not gated on USE_MOCK_DATA, which is a separate
  // "force mock everywhere" dev toggle used by the other functions in this
  // file): returning an empty array when the backend is unreachable is
  // strictly worse than showing something, and fetchAiRestaurants already
  // logs "falling back to mock data" on failure — this makes that true.
  return await fetchMockRestaurants(params);
}

// ── Existing functions (unchanged) ──────────────────────────────────────────

/**
 * Get restaurant details by ID
 * @param {string} restaurantId - Restaurant ID
 * @returns {Promise<Object>} Restaurant details
 */
export async function getRestaurantDetails(restaurantId) {
  if (!OPENTABLE_ENABLED) {
    return null;
  }

  if (USE_MOCK_DATA) {
    await new Promise(resolve => setTimeout(resolve, 300));
    return MOCK_RESTAURANTS.find(r => r.id === restaurantId || r.openTableId === restaurantId) || null;
  }

  try {
    const response = await authFetch(`${API_BASE_URL}/opentable/restaurants/${restaurantId}`);
    if (!response.ok) {
      throw new Error(`OpenTable API error: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error getting restaurant details:', error);
    throw error;
  }
}

/**
 * Check availability for a restaurant
 * @param {string} restaurantId - Restaurant ID
 * @param {string} date - Date in YYYY-MM-DD format
 * @param {number} partySize - Party size (1-8)
 * @param {string} time - Preferred time (HH:MM format, optional)
 * @returns {Promise<Array>} Array of available time slots
 */
export async function checkAvailability(restaurantId, date, partySize, time = null) {
  if (!OPENTABLE_ENABLED) {
    return [];
  }

  if (USE_MOCK_DATA) {
    await new Promise(resolve => setTimeout(resolve, 400));

    const slots = [];
    const baseHour = time ? parseInt(time.split(':')[0]) : 18;

    for (let i = -2; i <= 2; i++) {
      const hour = baseHour + i;
      if (hour >= 11 && hour <= 22) {
        slots.push({
          time: `${hour.toString().padStart(2, '0')}:00`,
          available: true,
          partySize: partySize
        });
        if (hour < 22) {
          slots.push({
            time: `${hour.toString().padStart(2, '0')}:30`,
            available: i !== 0,
            partySize: partySize
          });
        }
      }
    }

    return slots.filter(s => s.available);
  }

  try {
    const queryParams = new URLSearchParams({
      date,
      partySize: partySize.toString()
    });
    if (time) {
      queryParams.append('time', time);
    }

    const response = await authFetch(`${API_BASE_URL}/opentable/restaurants/${restaurantId}/availability?${queryParams}`);
    if (!response.ok) {
      throw new Error(`OpenTable API error: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error checking availability:', error);
    throw error;
  }
}

/**
 * Create a reservation.
 * After a successful reservation, sends a non-blocking `dining_booking`
 * learning signal to the taste profile service (Task 5.5).
 *
 * @param {Object} reservationData - Reservation data
 * @returns {Promise<Object>} Reservation confirmation
 */
export async function createReservation(reservationData) {
  if (!OPENTABLE_ENABLED) {
    throw new Error('OpenTable integration is disabled');
  }

  if (USE_MOCK_DATA) {
    await new Promise(resolve => setTimeout(resolve, 800));

    const confirmationNumber = `MOCK-${Date.now()}-${Math.random().toString(36).substr(2, 9).toUpperCase()}`;

    const result = {
      success: true,
      confirmationNumber,
      reservationId: `mock-reservation-${Date.now()}`,
      restaurant: MOCK_RESTAURANTS.find(r => r.id === reservationData.restaurantId || r.openTableId === reservationData.restaurantId),
      date: reservationData.date,
      time: reservationData.time,
      partySize: reservationData.partySize,
      customer: reservationData.customer,
      status: 'confirmed',
      createdAt: new Date().toISOString()
    };

    // Task 5.5: Non-blocking learning signal
    _sendDiningBookingSignal(reservationData);

    return result;
  }

  // Real API call
  try {
    const response = await authFetch(`${API_BASE_URL}/opentable/reservations`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(reservationData)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || `OpenTable API error: ${response.status}`);
    }

    const result = await response.json();

    // Task 5.5: Non-blocking learning signal after successful reservation
    _sendDiningBookingSignal(reservationData);

    return result;
  } catch (error) {
    console.error('Error creating reservation:', error);
    throw error;
  }
}

/**
 * Send a non-blocking dining_booking learning signal to the taste profile.
 * Failures are logged but never propagated.
 * @param {Object} reservationData
 */
function _sendDiningBookingSignal(reservationData) {
  try {
    const signalData = {
      cuisine: reservationData.cuisine || reservationData.restaurant?.cuisine || '',
      priceRange: reservationData.priceRange || reservationData.restaurant?.priceRange || '',
      location: reservationData.location || reservationData.restaurant?.address || '',
    };
    // Fire-and-forget — do not await
    learnSignal('dining_booking', signalData).catch((err) => {
      console.warn('[OpenTableService] dining_booking signal failed:', err.message);
    });
  } catch (err) {
    console.warn('[OpenTableService] Error preparing dining_booking signal:', err.message);
  }
}

/**
 * Get reservation details
 * @param {string} reservationId - Reservation ID or confirmation number
 * @returns {Promise<Object>} Reservation details
 */
export async function getReservation(reservationId) {
  if (!OPENTABLE_ENABLED) {
    return null;
  }

  if (USE_MOCK_DATA) {
    await new Promise(resolve => setTimeout(resolve, 300));
    return null;
  }

  try {
    const response = await authFetch(`${API_BASE_URL}/opentable/reservations/${reservationId}`);
    if (!response.ok) {
      throw new Error(`OpenTable API error: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error getting reservation:', error);
    throw error;
  }
}

/**
 * Cancel a reservation
 * @param {string} reservationId - Reservation ID or confirmation number
 * @returns {Promise<Object>} Cancellation confirmation
 */
export async function cancelReservation(reservationId) {
  if (!OPENTABLE_ENABLED) {
    throw new Error('OpenTable integration is disabled');
  }

  if (USE_MOCK_DATA) {
    await new Promise(resolve => setTimeout(resolve, 500));
    return {
      success: true,
      reservationId,
      status: 'cancelled',
      cancelledAt: new Date().toISOString()
    };
  }

  try {
    const response = await authFetch(`${API_BASE_URL}/opentable/reservations/${reservationId}`, {
      method: 'DELETE'
    });

    if (!response.ok) {
      throw new Error(`OpenTable API error: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error cancelling reservation:', error);
    throw error;
  }
}

export default {
  searchRestaurants,
  getRestaurantDetails,
  checkAvailability,
  createReservation,
  getReservation,
  cancelReservation
};
